/**
 * Organization profile: field definitions and conversions, shared by the admin
 * "New organization" dialog and the customer's Settings page. Mirrors
 * backend/models/organization.py (the server validates everything again).
 *
 * Field types: text, url, email, number, select, suggest (free text with
 * suggestions), tags (free list), chips (pick from presets, add your own),
 * yesno, textarea.
 */

export const SECTIONS = [
  {
    key: 'identity', title: 'Identity', hint: 'Who the organization is and which domains it owns.',
    fields: [
      { key: 'legal_name', label: 'Legal name', type: 'text', max: 200 },
      { key: 'website', label: 'Website', type: 'url', placeholder: 'https://example.com' },
      { key: 'primary_domains', label: 'Primary domains', type: 'tags', placeholder: 'example.com, example.org', hint: 'Used to monitor breaches and to recognise your assets in reporting.', wide: true },
      { key: 'brand_names', label: 'Brand and trading names', type: 'tags', placeholder: 'Acme Bank, Acme Pay', hint: 'Names other than the company name that should be watched, for example on ransomware leak sites.', wide: true },
      { key: 'hq_country', label: 'Headquarters country', type: 'text', max: 200 },
      { key: 'founded_year', label: 'Founded', type: 'number', min: 1800, max: 2100, placeholder: 'Year' },
      { key: 'ownership', label: 'Ownership', type: 'select', options: 'ownership' },
      { key: 'parent_organization', label: 'Parent organization', type: 'text', max: 200 },
    ],
  },
  {
    key: 'scale', title: 'Scale and footprint', hint: 'How large the organization is and where it operates.',
    fields: [
      { key: 'it_staff', label: 'IT staff', type: 'number', min: 0 },
      { key: 'security_staff', label: 'Security staff', type: 'number', min: 0 },
      { key: 'locations', label: 'Office and site locations', type: 'number', min: 0 },
      { key: 'remote_workforce_pct', label: 'Remote workforce (%)', type: 'number', min: 0, max: 100 },
      { key: 'revenue_band', label: 'Annual revenue', type: 'select', options: 'revenue_bands' },
      { key: 'countries', label: 'Countries of operation', type: 'tags', placeholder: 'Germany, France, Japan', wide: true },
    ],
  },
  {
    key: 'sector', title: 'Sector and compliance', hint: 'What the organization does, who regulates it and what data it handles.',
    fields: [
      { key: 'sub_industry', label: 'Sub-industry', type: 'text', placeholder: 'e.g. Retail banking, Medical devices' },
      { key: 'data_residency', label: 'Data residency requirements', type: 'text', placeholder: 'e.g. EU only' },
      { key: 'customer_base', label: 'Customer base', type: 'chips', options: 'customer_base', wide: true },
      { key: 'critical_infrastructure', label: 'Designated critical infrastructure', type: 'yesno' },
      { key: 'critical_infrastructure_sector', label: 'Critical infrastructure sector', type: 'select', options: 'critical_sectors', showIf: (s) => s.critical_infrastructure === true },
      { key: 'regulators', label: 'Regulators and authorities', type: 'tags', placeholder: 'SAMA, FCA, HHS', wide: true },
      { key: 'data_types', label: 'Sensitive data handled', type: 'chips', options: 'data_types', wide: true },
    ],
  },
  {
    key: 'technology', title: 'Technology footprint', hint: 'Products and platforms you run. A story that names one of them is matched to you, whatever its industry tags.',
    fields: [
      { key: 'cloud_providers', label: 'Cloud providers', type: 'chips', options: 'cloud_providers', wide: true },
      { key: 'operating_systems', label: 'Operating systems', type: 'chips', options: 'operating_systems', wide: true },
      { key: 'identity_provider', label: 'Identity provider', type: 'suggest', options: 'identity_providers' },
      { key: 'email_platform', label: 'Email platform', type: 'suggest', options: 'email_platforms' },
      { key: 'network_vendors', label: 'Network and firewall vendors', type: 'tags', placeholder: 'Palo Alto, Fortinet, Cisco', wide: true },
      { key: 'business_applications', label: 'Key business applications', type: 'tags', placeholder: 'SAP, Salesforce, Oracle EBS', wide: true },
      { key: 'key_vendors', label: 'Other critical vendors and suppliers', type: 'tags', placeholder: 'Snowflake, CrowdStrike', wide: true },
      { key: 'third_party_reliance', label: 'Reliance on third parties', type: 'select', options: 'third_party_reliance' },
      { key: 'has_ot_ics', label: 'Operates OT / ICS systems', type: 'yesno' },
      { key: 'internet_facing_notes', label: 'Internet-facing assets', type: 'textarea', placeholder: 'VPN gateways, customer portals, exposed APIs…', wide: true },
    ],
  },
  {
    key: 'security', title: 'Security program', hint: 'How security is run today.',
    fields: [
      { key: 'soc_model', label: 'SOC model', type: 'select', options: 'soc_models' },
      { key: 'soc_hours', label: 'SOC coverage', type: 'select', options: 'soc_hours' },
      { key: 'siem', label: 'SIEM', type: 'text', placeholder: 'e.g. Splunk, Sentinel' },
      { key: 'edr', label: 'EDR / XDR', type: 'text', placeholder: 'e.g. CrowdStrike, Defender' },
      { key: 'soar', label: 'SOAR', type: 'text' },
      { key: 'maturity', label: 'Security maturity (self-assessed)', type: 'select', choices: [['1', '1 · Initial'], ['2', '2 · Developing'], ['3', '3 · Defined'], ['4', '4 · Managed'], ['5', '5 · Optimized']], numeric: true },
      { key: 'ir_retainer', label: 'Incident response retainer', type: 'yesno' },
      { key: 'cyber_insurance', label: 'Cyber insurance', type: 'yesno' },
      { key: 'frameworks', label: 'Security frameworks followed', type: 'chips', options: 'frameworks', wide: true },
    ],
  },
  {
    key: 'risk', title: 'Risk context', hint: 'What matters most and what has already happened. This is what makes briefs relevant, so be specific.',
    fields: [
      { key: 'crown_jewels', label: 'Crown jewels', type: 'tags', placeholder: 'Core banking platform, patient records, source code', hint: 'The assets whose loss would hurt most.', wide: true },
      { key: 'top_concerns', label: 'Top threat concerns', type: 'chips', options: 'concerns', wide: true },
      { key: 'threat_actors_of_concern', label: 'Threat actors of concern', type: 'tags', placeholder: 'LockBit, APT29', wide: true },
      { key: 'past_incidents', label: 'Security incidents in the last 24 months', type: 'textarea', placeholder: 'What happened, when and how it was resolved…', wide: true },
    ],
  },
  {
    key: 'contacts', title: 'Security contacts', hint: 'Who should receive critical alerts.',
    fields: [
      { key: 'security_contact_name', label: 'Primary security contact', type: 'text' },
      { key: 'security_contact_email', label: 'Primary contact email', type: 'email' },
      { key: 'escalation_contact_name', label: 'Escalation contact', type: 'text' },
      { key: 'escalation_contact_email', label: 'Escalation email', type: 'email' },
    ],
  },
];

export const INTERNAL_SECTION = {
  key: 'internal', title: 'Internal notes', hint: 'Only visible to IntelliSecure staff. Never shown to the customer.',
  fields: [
    { key: 'account_owner', label: 'Account owner', type: 'text' },
    { key: 'plan', label: 'Plan', type: 'select', options: 'plans' },
    { key: 'notes', label: 'Notes', type: 'textarea', wide: true },
  ],
};

const isEmpty = (v) => v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0);

export function emptyOrg() {
  const insights = {};
  [...SECTIONS, INTERNAL_SECTION].forEach((s) => {
    insights[s.key] = {};
    s.fields.forEach((f) => {
      insights[s.key][f.key] = f.type === 'tags' || f.type === 'chips' ? [] : (f.type === 'yesno' ? null : '');
    });
  });
  return {
    owner_email: '', owner_name: '',
    company_name: '', industry: '', region: '', company_size: '', num_employees: '',
    applied_policies: [], security_solutions: [], restrictions: [],
    insights,
  };
}

/** A profile from the API (possibly sparse, possibly an older tenant) as form state. */
export function fromProfile(profile) {
  const base = emptyOrg();
  const p = profile || {};
  const insights = {};
  Object.keys(base.insights).forEach((section) => {
    insights[section] = { ...base.insights[section], ...((p.insights || {})[section] || {}) };
    Object.keys(insights[section]).forEach((k) => {
      if (insights[section][k] === null && base.insights[section][k] !== null) insights[section][k] = base.insights[section][k];
    });
  });
  return {
    ...base,
    company_name: p.company_name || '',
    industry: p.industry || '',
    region: p.region || '',
    company_size: p.company_size || '',
    num_employees: p.num_employees ?? '',
    applied_policies: p.applied_policies || [],
    security_solutions: p.security_solutions || [],
    restrictions: p.restrictions || [],
    insights,
  };
}

/** Form state as the API wants it: empty numbers become null, text is trimmed. */
export function toPayload(form, { includeInternal = true } = {}) {
  const insights = {};
  [...SECTIONS, ...(includeInternal ? [INTERNAL_SECTION] : [])].forEach((s) => {
    insights[s.key] = {};
    s.fields.forEach((f) => {
      let v = form.insights?.[s.key]?.[f.key];
      if (f.type === 'number' || f.numeric) v = isEmpty(v) ? null : Number(v);
      else if (typeof v === 'string') v = v.trim();
      insights[s.key][f.key] = v;
    });
  });
  return {
    company_name: form.company_name.trim(),
    industry: form.industry,
    region: form.region,
    company_size: form.company_size,
    num_employees: form.num_employees === '' || form.num_employees == null ? 0 : Number(form.num_employees),
    applied_policies: form.applied_policies,
    security_solutions: form.security_solutions,
    restrictions: form.restrictions,
    insights,
  };
}

/** Filled / total for one section, counting only fields that are currently shown. */
export function sectionProgress(section, values) {
  const shown = section.fields.filter((f) => !f.showIf || f.showIf(values || {}));
  const filled = shown.filter((f) => {
    const v = (values || {})[f.key];
    return !isEmpty(v);
  }).length;
  return { filled, total: shown.length };
}

/** Map FastAPI 422 details to { 'insights.identity.website': 'message' }. */
export function fieldErrors(err) {
  const detail = err?.response?.data?.detail;
  const out = {};
  if (!Array.isArray(detail)) return out;
  detail.forEach((d) => {
    const loc = (d.loc || []).filter((x) => x !== 'body');
    out[loc.join('.')] = String(d.msg || '').replace(/^Value error, /, '');
  });
  return out;
}
